import React from "react";
import { useTopics } from "@client/hooks/useTopics";
import { colors, suspiciousColor } from "@client/constants";
import { nickname } from "@client/utils/nickname";
import { fix, number, unit, unundefined } from "@client/utils/values";
import { Popover, PopoverContent, PopoverTrigger } from "@client/components/ui/popover";
import Dot from "./Dot";

type AverageValueProps = {
  topicIds: string[];
  boolean?: boolean;
};

const AverageValue: React.FC<AverageValueProps> = ({ topicIds }) => {
  const { values, topics, suspicious, lastValueIntervals, animationDurations } = useTopics(topicIds);

  const numericVals = Object.values(values).filter((val): val is number => typeof val === "number");
  const average = numericVals.length > 0 ? numericVals.reduce((a, b) => +a + +b, 0) / numericVals.length : NaN;
  const anySuspicious = Object.values(suspicious).some((x) => x);
  const firstTopicKey = Object.keys(topics)[0];
  const firstTopic = firstTopicKey ? topics[firstTopicKey] : undefined;

  const numericIntervals = Object.values(lastValueIntervals).filter((v): v is number => v !== undefined);
  const avgInterval =
    numericIntervals.length > 0 ? Math.round(numericIntervals.reduce((a, b) => a + b, 0) / numericIntervals.length) : 0;
  const numericDurations = Object.values(animationDurations);
  const avgDuration =
    numericDurations.length > 0 ? Math.round(numericDurations.reduce((a, b) => a + b, 0) / numericDurations.length) : 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`text-xl font-semibold ${anySuspicious ? "underline decoration-amber-600" : ""}`}
          style={{ color: anySuspicious ? suspiciousColor : colors[0] }}
        >
          <Dot duration={avgDuration} interval={avgInterval} />
          {unit(unundefined(fix(number(String(average)), firstTopic?.decimals ?? 1), "---"), firstTopic?.unit ?? "")}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto min-w-56" align="start">
        <ul className="flex flex-col gap-1.5">
          {topicIds.map((topicId, i) => (
            <li key={topicId} className="flex items-center justify-between gap-3">
              <span className="text-sm flex items-center">
                <Dot duration={animationDurations[topicId] ?? 0} interval={lastValueIntervals[topicId] ?? 0} />
                {nickname(topics[topicId])}
              </span>
              <span
                className={`font-semibold tabular-nums ${suspicious[topicId] ? "line-through decoration-amber-600" : ""}`}
                style={{ color: suspicious[topicId] ? suspiciousColor : colors[i] }}
              >
                {unit(
                  unundefined(fix(number(String(values[topicId])), topics[topicId]?.decimals ?? 1), "---"),
                  topics[topicId]?.unit ?? ""
                )}
              </span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

export default React.memo(AverageValue);
